<template>
  <div class="device-ssd-health">
    <v-progress-circular v-if="loading" color="primary" indeterminate></v-progress-circular>

    <template v-else-if="drives.length">
      <v-row v-for="drive in drives" :key="drive.id" dense>
        <v-col cols="12">
          <div class="d-flex flex-wrap align-center" style="gap: 4px 24px">
            <div v-for="detail in drive.details" :key="detail.label" class="d-flex align-center">
              <span class="font-weight-medium me-2">{{ detail.label }}:</span>
              <v-chip v-if="detail.chipColor" :color="detail.chipColor" text-color="white" x-small label>
                {{ detail.value }}
              </v-chip>
              <span v-else class="text--secondary">{{ detail.value }}</span>
            </div>
          </div>
        </v-col>
      </v-row>

      <v-row class="mt-2 mb-0">
        <v-col cols="12" class="py-0 d-flex align-center flex-wrap">
          <span class="text-caption me-2">Fields:</span>
          <v-chip
            v-for="field in availableFields"
            :key="field.key"
            small
            class="mr-2 mb-1"
            :color="fieldColor(field.key)"
            :outlined="!isFieldEnabled(field.key)"
            :text-color="isFieldEnabled(field.key) ? fieldTextColor(field.key) : fieldColor(field.key)"
            @click="toggleField(field.key)"
          >
            {{ field.label }}
          </v-chip>
        </v-col>
      </v-row>

      <v-row v-if="chartSeries.length">
        <v-col cols="12">
          <vue-apex-charts type="line" height="400" :options="chartOptions" :series="chartSeries"></vue-apex-charts>
        </v-col>
      </v-row>
      <v-row v-else>
        <v-col cols="12">
          <span class="text--secondary">Select at least one field to plot.</span>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-data-table
            :headers="tableHeaders"
            :items="tableItems"
            :items-per-page="10"
            sort-by="date_time"
            sort-desc
            dense
          >
            <template v-slot:item.date_time="{ item }">
              <span class="text-no-wrap">{{ item.date_time }}</span>
            </template>
            <template v-for="field in enabledFields" v-slot:[`item.${field.key}`]="{ item }">
              <span :key="field.key">{{ formatValue(item[field.key]) }}</span>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </template>

    <span v-else class="text--secondary">{{ emptyMessage }}</span>
  </div>
</template>

<script>
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import timezone from "dayjs/plugin/timezone"
import VueApexCharts from "vue-apexcharts"
import { getSeriesColor, getSeriesTextColor } from "@/utils/chartColors"
import {
  SSD_FIELDS,
  SSD_AXIS_LABELS,
  availableSsdFields,
  formatSsdValue,
  groupSsdPointsByDrive,
  toSsdPoints,
  toSsdSeriesData,
} from "@/utils/ssdHealth"

dayjs.extend(utc)
dayjs.extend(timezone)

export default {
  name: "DeviceSsdHealth",

  components: {
    VueApexCharts,
  },

  props: {
    records: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    rangeStart: {
      type: Number,
      default: null,
    },
    rangeEnd: {
      type: Number,
      default: null,
    },
    emptyMessage: {
      type: String,
      default: 'Click "Go" above to load SSD health for the selected range.',
    },
  },

  data() {
    return {
      fieldStates: {},
    }
  },

  computed: {
    points() {
      return toSsdPoints(this.records)
    },

    drives() {
      return groupSsdPointsByDrive(this.points).map(drive => ({
        ...drive,
        details: this.driveDetails(drive.summary),
      }))
    },

    availableFields() {
      return availableSsdFields(this.points)
    },

    enabledFields() {
      return this.availableFields.filter(field => this.isFieldEnabled(field.key))
    },

    chartSeries() {
      const multipleDrives = this.drives.length > 1

      return this.drives.flatMap((drive, driveIndex) =>
        this.enabledFields.map(field => ({
          name: multipleDrives ? `${field.label} · ${drive.id}` : field.label,
          data: toSsdSeriesData(drive.points, field.key),
          axis: field.axis,
          fieldKey: field.key,
          driveIndex,
        })),
      )
    },

    chartOptions() {
      const formatNZ = (value, format) => dayjs(value).tz("Pacific/Auckland").format(format)

      // Percentages, terabytes and erase counts share a chart but not a scale,
      // so each group of fields gets its own axis and every other series is
      // pinned to the first series using that axis
      const axisLeaders = {}
      this.chartSeries.forEach(series => {
        if (!axisLeaders[series.axis]) {
          axisLeaders[series.axis] = series.name
        }
      })
      const axisOrder = Object.keys(axisLeaders)

      return {
        chart: {
          type: "line",
          toolbar: { show: true },
          zoom: { enabled: true },
        },
        colors: this.chartSeries.map(series => this.fieldColor(series.fieldKey)),
        stroke: {
          curve: "straight",
          width: 1,
          dashArray: this.chartSeries.map(series => series.driveIndex * 4),
        },
        markers: { size: 2, strokeWidth: 0 },
        xaxis: {
          type: "datetime",
          min: this.rangeStart ?? undefined,
          max: this.rangeEnd ?? undefined,
          labels: {
            datetimeUTC: false,
            formatter: value => formatNZ(value, "DD MMM HH:mm"),
          },
        },
        yaxis: this.chartSeries.map(series => {
          const isLeader = axisLeaders[series.axis] === series.name

          return {
            seriesName: axisLeaders[series.axis],
            show: isLeader,
            opposite: axisOrder.indexOf(series.axis) % 2 === 1,
            title: { text: isLeader ? SSD_AXIS_LABELS[series.axis] : undefined },
            labels: { formatter: formatSsdValue },
          }
        }),
        legend: { show: true, position: "top" },
        tooltip: {
          x: {
            formatter: value => formatNZ(value, "YYYY-MM-DD HH:mm"),
          },
          y: {
            formatter: formatSsdValue,
          },
        },
      }
    },

    tableHeaders() {
      const headers = [{ text: "Date/Time", value: "date_time" }]

      if (this.drives.length > 1) {
        headers.push({ text: "Drive", value: "drive" })
      }

      this.enabledFields.forEach(field => {
        headers.push({ text: field.label, value: field.key, align: "end" })
      })

      return headers
    },

    tableItems() {
      return this.points.map(point => {
        const row = {
          date_time: point.date_time,
          drive: point.drive,
        }

        // Raw numbers so the column sorts numerically; the cell slot formats them
        this.enabledFields.forEach(field => {
          row[field.key] = point[field.key]
        })

        return row
      })
    },
  },

  methods: {
    formatValue: formatSsdValue,
    driveDetails(summary) {
      const details = [{ label: "Model", value: summary.model }]

      if (summary.modelFamily) {
        details.push({ label: "Family", value: summary.modelFamily })
      }

      details.push({ label: "Serial", value: summary.serial }, { label: "Firmware", value: summary.firmware })

      details.push({ label: "Remaining Life", value: `${formatSsdValue(summary.remainingLife)} %` })

      if (summary.capacity !== "-") {
        details.push({ label: "Capacity", value: summary.capacity })
      }

      details.push({ label: "Last reported", value: summary.lastReported })

      return details
    },
    isFieldEnabled(key) {
      if (this.fieldStates[key] !== undefined) {
        return this.fieldStates[key]
      }

      return SSD_FIELDS.some(field => field.key === key && field.enabled)
    },
    toggleField(key) {
      this.$set(this.fieldStates, key, !this.isFieldEnabled(key))
    },
    // Colours follow the field's position in the full list rather than the
    // chart, so a series keeps its colour as other fields are toggled on and off
    fieldIndex(key) {
      return SSD_FIELDS.findIndex(field => field.key === key)
    },
    fieldColor(key) {
      return getSeriesColor(this.fieldIndex(key))
    },
    fieldTextColor(key) {
      return getSeriesTextColor(this.fieldIndex(key))
    },
  },
}
</script>
